import { Stack } from "@mui/material";
import Konva from "konva";
import { forwardRef, useMemo, useRef, useState } from "react";
import { Stage } from "react-konva";
import { useEventCallback } from "usehooks-ts";

import type { AspectRatio, ImageTransform } from "@/types";

import { Area, Size } from "../../types";
import { ImageLayer } from "../ImageLayer";
import { OverlayLayer } from "../OverlayLayer";
import { Toolbar } from "../Toolbar";
import { centerContentInContainer } from "./utils/center";
import { getCenteredArea } from "./utils/clear-area";
import { getImageArea } from "./utils/image";
import { snapContentInContainer } from "./utils/snap";
import { applyTransform, extractTransform } from "./utils/transform";

interface ImageStageProps {
  image: HTMLImageElement;
  stageSize: Size;
  aspectRatio: AspectRatio;
  aspectRatios: AspectRatio[];
  onAspectRatioChange?: (aspectRatio: AspectRatio) => void;

  transform?: ImageTransform;
  onTransformChange?: (transform: ImageTransform) => void;
}

export type ImageStageRef = Record<string, never>;

export const ImageStage = forwardRef<ImageStageRef, ImageStageProps>(
  (props: ImageStageProps, _ref) => {
    const {
      image,
      stageSize = { width: 900, height: 480 },
      aspectRatio,
      aspectRatios,
      onAspectRatioChange,
      transform,
      onTransformChange,
    } = props;

    const stageRef = useRef<Konva.Stage>(null);

    // Calculate the clear area that fits the aspect ratio within the stage
    // The "clear area" is the area that is not "darkened" in the ImageEditor
    const clearArea = useMemo<Area>(
      () => getCenteredArea(aspectRatio, stageSize),
      [aspectRatio, stageSize],
    );

    const imageArea = useMemo<Area>(() => {
      if (!transform) return getImageArea(image, clearArea);

      return applyTransform(image, clearArea, transform);
    }, [image, clearArea, transform]);

    // State to track the current image area (for interactive changes)
    const [currentImageArea, setCurrentImageArea] = useState<Area>(imageArea);

    // Update currentImageArea when imageArea changes (from transform)
    // useMemo(() => {
    //   setCurrentImageArea(imageArea);
    // }, [imageArea]);

    // Handle image area changes from the Imaage component
    const handleImageAreaChange = useEventCallback((newImageArea: Area) => {
      setCurrentImageArea(newImageArea);

      if (!onTransformChange) return;

      // Convert back to transform and notify parent
      const newTransform = extractTransform(image, newImageArea, clearArea);
      onTransformChange(newTransform);
    });

    // Center the image horizontally or vertically
    const handleCenter = useEventCallback((axis: "x" | "y") => {
      const newImageArea = centerContentInContainer(
        currentImageArea,
        clearArea,
        axis,
      );

      handleImageAreaChange(newImageArea);
    });

    // Snap the image to fit the clear area width or height
    const handleSnap = useEventCallback((direction: "width" | "height") => {
      const newImageArea = snapContentInContainer(
        currentImageArea,
        clearArea,
        direction,
      );

      handleImageAreaChange(newImageArea);
    });

    return (
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Stage width={stageSize.width} height={stageSize.height} ref={stageRef}>
          <ImageLayer
            image={image}
            imageArea={currentImageArea}
            onImageAreaChange={handleImageAreaChange}
          />

          <OverlayLayer
            area={clearArea}
            stageWidth={stageSize.width}
            stageHeight={stageSize.height}
          />
        </Stage>

        <Toolbar
          onSnap={handleSnap}
          onCenter={handleCenter}
          aspectRatios={aspectRatios}
          aspectRatio={aspectRatio}
          onAspectRatioChange={onAspectRatioChange}
        />
      </Stack>
    );
  },
);
